import type { ArchetypePassive, ElementType, PlayerTacticalProfile, Position, Tactics } from '../../types';
import type { Vector2 } from '../match/Vector2';

export interface PlayerStats {
  kick: number;
  dribble: number;
  pass: number;
  defense: number;
  speed: number;
  power: number;
  control: number;
  stamina: number;
}

export type EngineTechniqueType = 'SHOT' | 'DRIBBLE' | 'BLOCK' | 'CATCH' | 'PASS' | 'BUFF';

export interface EngineTechnique {
  id: string;
  name: string;
  type: EngineTechniqueType;
  element: ElementType | 'VOID';
  power: number;
  tpCost: number;
  minTp: number;
}

export interface EnginePlayer {
  /** Unique only inside one match. A catalog player can appear for both sides. */
  id: string;
  /** Stable catalog identity used by UI and persistence. */
  sourcePlayerId?: string;
  fullName: string;
  alias: string;
  position: Position;
  element: ElementType;
  passive: ArchetypePassive | null;
  tacticalProfile: PlayerTacticalProfile;
  stats: PlayerStats;
  tpMax: number;
  equippedTechniques: string[];
}

export interface EngineTeam {
  id: string;
  name: string;
  side: 0 | 1;
  tactics: Tactics & Record<string, number>;
  players: EnginePlayer[];
  roles: string[];
  formationPositions: Vector2[];
}

export interface UtilityAiConfig {
  selectionTemperature: number;
  scoreMinimum: number;
  scoreMaximum: number;
  pass: {
    actorPassWeight: number;
    actorControlWeight: number;
    receiverControlWeight: number;
    receiverSpeedWeight: number;
    receiverFallbackStat: number;
    tacticalWeight: number;
    makingRunBonus: number;
    supportingRunBonus: number;
    momentumWeight: number;
    forwardGainWeight: number;
    forwardIntentMinimum: number;
    forwardIntentMaximum: number;
    backwardBasePenalty: number;
    backwardDistanceWeight: number;
    backwardMaximumPenalty: number;
    finalThirdBackwardPenalty: number;
    strikerToDefenderPenalty: number;
    goalkeeperRecyclePenalty: number;
    shootingZoneRecyclePenalty: number;
    buildUpLinkBonus: number;
    directForwardBuildUpPenalty: number;
    fullBackOutletBonus: number;
    throughBallBonus: number;
    purposefulLongPassBonus: number;
    recycleSupportBonus: number;
    pressurePenaltyWeight: number;
    riskPenaltyWeight: number;
    carryLanePassProgressThreshold: number;
    carryLaneShortPassPenalty: number;
    carryLaneModeratePassPenalty: number;
  };
  dribble: {
    dribbleWeight: number;
    controlWeight: number;
    speedWeight: number;
    powerWeight: number;
    momentumWeight: number;
    staminaDeficitPenaltyWeight: number;
    pressurePenaltyWeight: number;
    localSpaceBonusWeight: number;
    finalThirdBonus: number;
    defensivePatiencePenalty: number;
    middlePatiencePenalty: number;
    finalPatiencePenalty: number;
    midfielderCarryBonus: number;
    midfielderOpenLaneCarryBonus: number;
    midfielderFavorableDribbleBonus: number;
    midfielderCarrySkillThreshold: number;
    carryProgressWeight: number;
    midfielderDirectDriveBonus: number;
    openLaneDepth: number;
    openLaneHalfWidth: number;
    favorableDribbleMargin: number;
    wideDefenderCarryBonus: number;
    carrySpaceThreshold: number;
  };
  shoot: {
    kickWeight: number;
    controlWeight: number;
    powerWeight: number;
    momentumWeight: number;
    distancePenaltyWeight: number;
    pressurePenaltyWeight: number;
    goalAngleBonusWeight: number;
    base: number;
    forwardPositionBonus: number;
    midfielderPositionBonus: number;
    defenderPositionBonus: number;
    goalkeeperPositionBonus: number;
    finalThirdBonus: number;
    veryCloseRangeBonus: number;
    closeRangeBonus: number;
    crossFinishBonus: number;
    stagnationFinishBonus: number;
    congestedForwardShotPenalty: number;
  };
  clearBall: { base: number; defensiveZoneBonus: number; pressureBonusWeight: number };
  tackle: {
    defenseWeight: number;
    powerWeight: number;
    speedWeight: number;
    controlWeight: number;
    pressureBonusWeight: number;
  };
  technique: {
    powerWeight: number;
    lateLosingBonus: number;
    lateDrawingBonus: number;
    lateWinningBonus: number;
    losingBonus: number;
    finalThirdShotBonus: number;
    forwardShotBonus: number;
    attackingDribbleBonus: number;
    momentumWeight: number;
    tpCostPenaltyWeight: number;
    earlyPreservePenalty: number;
    lowReserveLatePenalty: number;
    lowReservePenalty: number;
    winningReservePenalty: number;
  };
  holdPosition: { base: number; tiredBonus: number };
  fallbackScore: number;
  repetition: {
    sameActionTargetPenaltyWeight: number;
    samePassTargetPenaltyWeight: number;
    immediateReturnPenalty: number;
  };
  tactical: {
    base: number;
    forwardGainWeight: number;
    idealSpacing: number;
    spacingPenaltyWeight: number;
    minimum: number;
    maximum: number;
  };
  teamTactics: {
    passTempoWeight: number;
    buildUpPassBonus: number;
    progressionPassBonus: number;
    dribbleRiskWeight: number;
    transitionDribbleBonus: number;
    shootRiskWeight: number;
    shootMentalityWeight: number;
    finalThirdShootBonus: number;
    tacklePressingWeight: number;
    techniqueAggressionWeight: number;
  };
}

export interface MatchEngineConfig {
  durationSeconds: number;
  /** TP restored by every on-pitch player for each simulated minute. */
  tpRegenerationPerMinute: number;
  movementStepSeconds: number;
  decisionIntervalSeconds: number;
  snapshotIntervalSeconds: number;
  minPlayerSpeed: number;
  maxPlayerSpeed: number;
  speedAttributeExponent: number;
  acceleration: number;
  minimumTurnRateRadiansPerSecond: number;
  maximumTurnRateRadiansPerSecond: number;
  brakingAccelerationMultiplier: number;
  minimumReactionSeconds: number;
  maximumReactionSeconds: number;
  passBallSpeed: number;
  shotBallSpeed: number;
  looseBallFriction: number;
  interceptionRadius: number;
  controlRadius: number;
  minimumTeamShotIntervalSeconds: number;
  minimumTeamTackleIntervalSeconds: number;
  minimumTeamTechniqueIntervalSeconds: number;
  minimumPlayerTechniqueIntervalSeconds: number;
  passActionCooldownSeconds: number;
  receptionActionCooldownSeconds: number;
  crossReceptionActionCooldownSeconds: number;
  receptionControlThreshold: number;
  dribbleActionCooldownSeconds: number;
  shotActionCooldownSeconds: number;
  passPreparationSeconds: number;
  dribblePreparationSeconds: number;
  shotPreparationSeconds: number;
  techniquePreparationSeconds: number;
  recentActionMemorySize: number;
  repeatedPassPairLimit: number;
  repeatedPassPairPenalty: number;
  repeatedActionCyclePenalty: number;
  stagnationPassLimit: number;
  stagnationMinimumProgress: number;
  stagnationNonProgressivePenalty: number;
  stagnationDribbleBonus: number;
  transitionWindowSeconds: number;
  defensiveCoverDistance: number;
  markingDistance: number;
  intentMaximumDurationSeconds: number;
  returnToShapeMinimumSeconds: number;
  anchorSoftness: number;
  reachableTargetHorizonSeconds: number;
  emergencyClearPressure: number;
  closeRangeShotDistance: number;
  minimumFinishingShotIntervalSeconds: number;
  minimumDribbleGoalDistance: number;
  crossFinishWindowSeconds: number;
  maximumForwardRunProgress: number;
  recoveryWinnerCooldownSeconds: number;
  recoveryLoserCooldownSeconds: number;
  dribbleBypassSeconds: number;
  techniqueDribbleBypassSeconds: number;
  tackleStaggerSeconds: number;
  techniqueBlockStaggerSeconds: number;
  duelFoulProbability: number;
  utilityAi: UtilityAiConfig;
  furyDurationSeconds: number;
  furyStatMultiplier: number;
  furyMaximumEffectiveStat: number;
  furyStaminaDrainMultiplier: number;
  furyMinimumActivationMinute: number;
  furyForcedActivationMinute: number;
  furyActivationUtilityThreshold: number;
  elementAdvantage: number;
  elementNeutral: number;
  elementDisadvantage: number;
}

export const DEFAULT_MATCH_ENGINE_CONFIG: MatchEngineConfig = {
  durationSeconds: 90 * 60,
  tpRegenerationPerMinute: 2,
  movementStepSeconds: 1 / 30,
  decisionIntervalSeconds: 1.05,
  snapshotIntervalSeconds: 1,
  minPlayerSpeed: 0.18,
  maxPlayerSpeed: 0.7,
  speedAttributeExponent: 1.35,
  acceleration: 0.95,
  minimumTurnRateRadiansPerSecond: 2.4,
  maximumTurnRateRadiansPerSecond: 5.8,
  brakingAccelerationMultiplier: 1.75,
  minimumReactionSeconds: 0.08,
  maximumReactionSeconds: 0.42,
  passBallSpeed: 3.8,
  shotBallSpeed: 7.8,
  looseBallFriction: 0.82,
  interceptionRadius: 3.25,
  controlRadius: 1.1,
  minimumTeamShotIntervalSeconds: 330,
  minimumTeamTackleIntervalSeconds: 120,
  minimumTeamTechniqueIntervalSeconds: 125,
  minimumPlayerTechniqueIntervalSeconds: 300,
  passActionCooldownSeconds: 5.5,
  receptionActionCooldownSeconds: 5.2,
  crossReceptionActionCooldownSeconds: 1.1,
  receptionControlThreshold: 55,
  dribbleActionCooldownSeconds: 5.5,
  shotActionCooldownSeconds: 6,
  passPreparationSeconds: 0.45,
  dribblePreparationSeconds: 0.55,
  shotPreparationSeconds: 0.72,
  techniquePreparationSeconds: 0.6,
  recentActionMemorySize: 12,
  repeatedPassPairLimit: 2,
  repeatedPassPairPenalty: 100,
  repeatedActionCyclePenalty: 90,
  stagnationPassLimit: 3,
  stagnationMinimumProgress: 8,
  stagnationNonProgressivePenalty: 90,
  stagnationDribbleBonus: 22,
  transitionWindowSeconds: 8,
  defensiveCoverDistance: 9,
  markingDistance: 5,
  intentMaximumDurationSeconds: 14,
  returnToShapeMinimumSeconds: 3,
  anchorSoftness: 0.88,
  reachableTargetHorizonSeconds: 2.2,
  emergencyClearPressure: 0.72,
  closeRangeShotDistance: 12,
  // A normal team shot cadence stays conservative, but a new clear chance
  // should not be suppressed for almost five simulated minutes by the last
  // attempt. The decision system still requires an actual final-third chance.
  minimumFinishingShotIntervalSeconds: 110,
  minimumDribbleGoalDistance: 18,
  crossFinishWindowSeconds: 4,
  maximumForwardRunProgress: 91,
  recoveryWinnerCooldownSeconds: 0.55,
  recoveryLoserCooldownSeconds: 2.2,
  dribbleBypassSeconds: 3.2,
  techniqueDribbleBypassSeconds: 6.5,
  tackleStaggerSeconds: 2.8,
  techniqueBlockStaggerSeconds: 6.2,
  duelFoulProbability: 0.05,
  utilityAi: {
    selectionTemperature: 0.1,
    scoreMinimum: -100,
    scoreMaximum: 150,
    pass: {
      actorPassWeight: 0.45, actorControlWeight: 0.2, receiverControlWeight: 0.1, receiverSpeedWeight: 0.1,
      receiverFallbackStat: 50, tacticalWeight: 0.1, makingRunBonus: 4, supportingRunBonus: 2,
      momentumWeight: 0.05, forwardGainWeight: 0.92, forwardIntentMinimum: -18, forwardIntentMaximum: 20,
      backwardBasePenalty: 4, backwardDistanceWeight: 1.15, backwardMaximumPenalty: 28,
      finalThirdBackwardPenalty: 16, strikerToDefenderPenalty: 24, goalkeeperRecyclePenalty: 18,
      shootingZoneRecyclePenalty: 15, buildUpLinkBonus: 6, directForwardBuildUpPenalty: 22,
      fullBackOutletBonus: 16,
      throughBallBonus: 24, purposefulLongPassBonus: 8, recycleSupportBonus: 32,
      pressurePenaltyWeight: 12, riskPenaltyWeight: 18,
      carryLanePassProgressThreshold: 11, carryLaneShortPassPenalty: 32, carryLaneModeratePassPenalty: 18,
    },
    dribble: {
      dribbleWeight: 0.4, controlWeight: 0.25, speedWeight: 0.15, powerWeight: 0.1,
      momentumWeight: 0.1, staminaDeficitPenaltyWeight: 0.12, pressurePenaltyWeight: 14,
      localSpaceBonusWeight: 22, finalThirdBonus: 7, defensivePatiencePenalty: 18,
      middlePatiencePenalty: 4, finalPatiencePenalty: 9,
      midfielderCarryBonus: 44, midfielderOpenLaneCarryBonus: 66, midfielderFavorableDribbleBonus: 34, midfielderCarrySkillThreshold: 58,
      carryProgressWeight: 3.2, midfielderDirectDriveBonus: 32,
      openLaneDepth: 17, openLaneHalfWidth: 11, favorableDribbleMargin: 5,
      wideDefenderCarryBonus: 38, carrySpaceThreshold: 0.55,
    },
    shoot: {
      kickWeight: 0.45, controlWeight: 0.15, powerWeight: 0.1, momentumWeight: 0.1,
      distancePenaltyWeight: 0.46, pressurePenaltyWeight: 10, goalAngleBonusWeight: 8, base: 17,
      forwardPositionBonus: 23, midfielderPositionBonus: 11, defenderPositionBonus: -7,
      goalkeeperPositionBonus: -90, finalThirdBonus: 17, veryCloseRangeBonus: 15, closeRangeBonus: 9,
      crossFinishBonus: 36, stagnationFinishBonus: 42, congestedForwardShotPenalty: 34,
    },
    clearBall: { base: 25, defensiveZoneBonus: 42, pressureBonusWeight: 25 },
    tackle: { defenseWeight: 0.45, powerWeight: 0.2, speedWeight: 0.15, controlWeight: 0.1, pressureBonusWeight: 10 },
    technique: {
      powerWeight: 0.27, lateLosingBonus: 30, lateDrawingBonus: 20, lateWinningBonus: 6,
      losingBonus: 12, finalThirdShotBonus: 14, forwardShotBonus: 8, attackingDribbleBonus: 5,
      momentumWeight: 0.1, tpCostPenaltyWeight: 0.3, earlyPreservePenalty: 2,
      lowReserveLatePenalty: 5, lowReservePenalty: 10, winningReservePenalty: 6,
    },
    holdPosition: { base: 14, tiredBonus: 18 },
    fallbackScore: 20,
    repetition: { sameActionTargetPenaltyWeight: 9, samePassTargetPenaltyWeight: 2.5, immediateReturnPenalty: 65 },
    tactical: { base: 50, forwardGainWeight: 1.4, idealSpacing: 22, spacingPenaltyWeight: 0.5, minimum: 0, maximum: 100 },
    teamTactics: {
      passTempoWeight: 4, buildUpPassBonus: 0.5, progressionPassBonus: 0.5,
      dribbleRiskWeight: 5, transitionDribbleBonus: 0.5, shootRiskWeight: 6,
      shootMentalityWeight: 4, finalThirdShootBonus: 0.5,
      tacklePressingWeight: 4, techniqueAggressionWeight: 30,
    },
  },
  furyDurationSeconds: 600,
  furyStatMultiplier: 1.1,
  furyMaximumEffectiveStat: 110,
  furyStaminaDrainMultiplier: 1.25,
  furyMinimumActivationMinute: 60,
  furyForcedActivationMinute: 86,
  furyActivationUtilityThreshold: 30,
  elementAdvantage: 1.15,
  elementNeutral: 1,
  elementDisadvantage: 0.9,
};
